import { Helmet } from 'react-helmet-async'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useScrollReveal, fadeUpVariants, staggerContainerVariants, cardVariants } from '../hooks/useScrollReveal'
import { useAdmin } from '../context/AdminContext'
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa'

const COLOR = {
  blue:   { accent:'#3b82f6', glow:'rgba(59,130,246,0.12)',  border:'rgba(59,130,246,0.25)'  },
  purple: { accent:'#10b981', glow:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
  cyan:   { accent:'#10b981', glow:'rgba(16,185,129,0.12)',   border:'rgba(16,185,129,0.25)'   },
  green:  { accent:'#10b981', glow:'rgba(16,185,129,0.12)',  border:'rgba(16,185,129,0.25)'  },
}

function ProjectCard({ project }) {
  const [hov, setHov] = useState(false)
  const c = COLOR[project.color] || COLOR.blue
  const tech = Array.isArray(project.tech)
    ? project.tech
    : (project.tech || '').split(',').map(t => t.trim()).filter(Boolean)

  return (
    <motion.div variants={cardVariants}
      onHoverStart={() => setHov(true)} onHoverEnd={() => setHov(false)}
      whileHover={{ y: -6, transition: { duration: 0.25, ease:[0.22,1,0.36,1] } }}
      className="card p-6 flex flex-col h-full transition-all duration-300"
      style={hov ? { borderColor: c.border, boxShadow: `var(--shadow-card), 0 20px 60px ${c.glow}` } : {}}>
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-xs text-muted">{String(project.id).padStart(2,'0')}</span>
        {project.featured && (
          <span className="px-2 py-0.5 rounded-lg font-mono text-xs"
            style={{ background: c.glow, color: c.accent, border:`1px solid ${c.border}` }}>Featured</span>
        )}
      </div>
      <h3 className="font-display font-bold text-lg text-primary mb-2 transition-colors"
        style={hov ? { color: c.accent } : {}}>
        {project.title}
      </h3>
      <p className="text-secondary text-sm leading-relaxed mb-4 flex-1">{project.description}</p>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {tech.map(t => (
          <span key={t} className="px-2.5 py-1 rounded-lg font-mono text-xs text-muted"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
            {t}
          </span>
        ))}
      </div>
      <div className="flex gap-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
        <motion.a href={project.github} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm text-secondary hover:text-primary transition-colors" whileHover={{ x: 2 }}>
          <FaGithub size={13} /> Source
        </motion.a>
      </div>
    </motion.div>
  )
}

export default function Portfolio() {
  const { ref, controls } = useScrollReveal()
  const { siteData, loading } = useAdmin()

  if (loading) return null
  return (
    <section id="portfolio" className="py-28 relative overflow-hidden">
      <Helmet>
        <title>Portfolio & Projects | Shiv Shankar Pandit</title>
        <meta name="description" content="A selection of my recent work and personal projects." />
      </Helmet>

      <div className="absolute top-1/4 -left-64 w-[500px] h-[500px] rounded-full blur-[120px] opacity-[0.06] pointer-events-none orb-blue" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div variants={staggerContainerVariants} initial="hidden" animate="visible">
          <motion.div variants={fadeUpVariants} className="mb-14">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <h1 className="section-heading">My <span className="gradient-text">Portfolio</span></h1>

            </div>
          </motion.div>
          <motion.div variants={staggerContainerVariants} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {siteData.projects.map(p => <ProjectCard key={p.id} project={p} />)}
          </motion.div>
          <motion.div variants={fadeUpVariants} className="mt-10 text-center">
            <a href="https://github.com/ShankarTheCoder" target="_blank" rel="noopener noreferrer"
              className="btn-outline inline-flex items-center gap-2 text-sm">
              <FaGithub size={13} /> See more on GitHub <FaExternalLinkAlt size={10} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
